import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { AddCircle, Delete, RemoveCircle } from "@mui/icons-material";

const VoucherProductModal = ({
  open,
  token,
  initialProducts = [],
  onSave,
  onClose,
}) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState(initialProducts);
  const [selecting, setSelecting] = useState(null);
  const [qty, setQty] = useState("");

  useEffect(() => {
    if (!open) return;
    setCart(initialProducts);
    setSelecting(null);
    setQty("");
    fetchMenus();
  }, [open]);

  const fetchMenus = async () => {
    setLoadingMenu(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-menu-with-archive`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const all = [
        ...(res.data.menu?.menuItems || []),
        ...(res.data.archive?.menuArchive || []),
      ];

      const parsed = all.map((m) => ({
        id: m._id || m.menuId,
        name: m.menuName,
        price: m.price ?? m.basePrice ?? 0,
        image: m.menuImage || "",
        category: m.categoryName || "Uncategorized",
        ingredients: m.menuIngredients || [],
        expanded: false,
      }));
      setMenuItems(parsed);
    } catch (err) {
      console.error("Failed to fetch menu:", err);
    } finally {
      setLoadingMenu(false);
    }
  };

  const addItemToCart = () => {
    const qNum = Number(qty);
    if (!qNum || qNum <= 0) return;

    setCart((prev) => {
      const existing = prev.find((p) => p.menuItemId === selecting.id);
      if (existing) {
        // merge quantities
        return prev.map((p) =>
          p.menuItemId === selecting.id
            ? { ...p, quantity: p.quantity + qNum }
            : p
        );
      }
      return [
        ...prev,
        {
          menuItemId: selecting.id,
          menuName: selecting.name,
          price: selecting.price,
          quantity: qNum,
        },
      ];
    });
    setSelecting(null);
    setQty("");
  };

  const updateQty = (id, delta) =>
    setCart((prev) =>
      prev.map((p) =>
        p.menuItemId === id
          ? { ...p, quantity: Math.max(1, p.quantity + delta) }
          : p
      )
    );

  const removeItem = (id) =>
    setCart((prev) => prev.filter((p) => p.menuItemId !== id));

  const toggleExpand = (id) =>
    setMenuItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, expanded: !it.expanded } : it))
    );

  const filtered = menuItems.filter((m) => {
    const t = search.toLowerCase();
    return (
      m.name.toLowerCase().includes(t) || m.category.toLowerCase().includes(t)
    );
  });

  const totalPrice = cart.reduce(
    (sum, { price, quantity }) => sum + price * quantity,
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>Select Products for Voucher</DialogTitle>

      <DialogContent
        dividers
        sx={{
          maxHeight: 420,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Search bar */}
        <TextField
          label="Search menu"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loadingMenu ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Avatar
                      variant="rounded"
                      src={item.image}
                      alt={item.name}
                      sx={{ width: 64, height: 64 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight="bold">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.category}
                      </Typography>
                      <Typography variant="body2">
                        ₱ {item.price.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  <Collapse in={item.expanded}>
                    {item.ingredients.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {item.ingredients.map((ing, i) => (
                          <Typography key={i} variant="body2" sx={{ pl: 2 }}>
                            - {ing.ingredientName} ({ing.amount})
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Collapse>

                  <Box
                    sx={{
                      mt: "auto",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setSelecting(item)}
                      variant="outlined"
                    >
                      Add
                    </Button>
                    <Button size="small" onClick={() => toggleExpand(item.id)}>
                      {item.expanded ? "Hide" : "Show"} ingredients
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogContent dividers sx={{ maxHeight: 220, overflowY: "auto" }}>
        <Typography variant="h6">Selected Products</Typography>
        {cart.length === 0 ? (
          <Typography>No products selected.</Typography>
        ) : (
          cart.map((p) => (
            <Box
              key={p.menuItemId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                my: 1,
              }}
            >
              <Typography>
                {p.menuName} – ₱{p.price} × {p.quantity}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => updateQty(p.menuItemId, -1)}
                >
                  <RemoveCircle fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => updateQty(p.menuItemId, 1)}
                >
                  <AddCircle fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeItem(p.menuItemId)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}

        <Typography sx={{ mt: 2 }} fontWeight="bold">
          Total Deduction: ₱ {totalPrice.toFixed(2)}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={cart.length === 0}
          onClick={() => {
            onSave(cart);
            onClose();
          }}
        >
          Save Products
        </Button>
      </DialogActions>

      <Dialog open={Boolean(selecting)} onClose={() => setSelecting(null)}>
        <DialogTitle>Quantity – {selecting?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Quantity"
            type="number"
            fullWidth
            autoFocus
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelecting(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={addItemToCart}
            disabled={!qty || Number(qty) <= 0}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default VoucherProductModal;
